import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_BASE_URL } from '../config/api';

// SVG Icons
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-1" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const Templates = {
  section: {
    title: "Introduction to ROS 2",
    slug: "intro-to-ros2",
    description: "Get started with ROS 2 core concepts and architecture.",
    overview: "This section introduces you to the basic tools and terms used in ROS 2.",
    icon: "🚀",
    order: 1,
    isActive: true,
    subsections: []
  },
  subsection: {
    title: "Understanding Nodes",
    slug: "understanding-nodes",
    description: "Nodes are the fundamental building blocks of a ROS 2 system.",
    overview: "Learn how nodes run, communicate, and form networks.",
    order: 1,
    isActive: true,
    subsections: [],
    subtopics: [
      {
        title: "Node Communication",
        slug: "node-communication",
        description: "How nodes talk to each other using topics, services, and actions.",
        order: 1,
        isActive: true,
        contentBlocks: [
          {
            type: "paragraph",
            content: "Nodes represent a single entity in the ROS 2 graph that performs a specific function. Nodes can publish and subscribe to topics.",
            links: []
          },
          {
            type: "heading",
            content: "Publishing a Topic",
            links: []
          },
          {
            type: "paragraph",
            content: "Here is an example code snippet in Python to create a publisher node:",
            links: []
          }
        ],
        codeExamples: [
          {
            language: "python",
            code: "import rclpy\nfrom rclpy.node import Node\n\nclass MinimalPublisher(Node):\n    def __init__(self):\n        super().__init__('minimal_publisher')\n        self.publisher_ = self.create_publisher(String, 'topic', 10)\n        ...",
            description: "A minimal publisher node in ROS 2",
            output: "Node started...",
            explanation: "Inherits from Node and initializes publisher."
          }
        ],
        images: []
      }
    ]
  },
  contentBlock: {
    type: "paragraph",
    content: "This is a paragraph block describing a key concept.",
    links: [
      {
        text: "Documentation Link",
        url: "https://docs.ros.org"
      }
    ]
  },
  codeExample: {
    language: "python",
    code: "print('Hello, ROS 2!')",
    description: "Sample description",
    output: "Hello, ROS 2!",
    explanation: "Sample explanation"
  }
};

// Helper function to generate slug from title
const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')     // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/-+/g, '-');  // Replace multiple - with single -
};

// Helper function to resolve a deep path inside a nested draft object
const resolvePath = (draft, path) => {
  let current = draft;
  for (let i = 0; i < path.length - 1; i++) {
    current = current[path[i]];
  }
  const lastKey = path[path.length - 1];
  return { parent: current, lastKey };
};

const CourseContent = () => {
  const [contents, setContents] = useState([]);
  const [courses, setCourses] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  
  // Course creation states
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [newCourseDesc, setNewCourseDesc] = useState('');
  const [newCourseCategory, setNewCourseCategory] = useState('');
  
  // Form fields
  const [formData, setFormData] = useState({
    courseId: '',
    courseKey: '',
    title: '',
    description: '',
    overview: '',
    isPublished: false,
    mainSectionsJson: '[]'
  });

  const [jsonError, setJsonError] = useState(null);
  const apiUrl = API_BASE_URL;

  // NEW: Visual builder states
  const [editorTab, setEditorTab] = useState('visual'); // 'visual' | 'json'
  const [mainSections, setMainSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedSubsections, setExpandedSubsections] = useState({});
  const [expandedSubtopics, setExpandedSubtopics] = useState({});
  const [subtopicTabs, setSubtopicTabs] = useState({}); // Stores selected subtopic panel (blocks, code, images)

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const resContents = await axios.get(`${apiUrl}/api/course-content`);
      const resCourses = await axios.get(`${apiUrl}/api/course-content/courses/list`);
      
      if (resContents.data.success) {
        setContents(resContents.data.data);
      }
      if (resCourses.data.success) {
        setCourses(resCourses.data.data.courses);
        setCategories(resCourses.data.data.categories);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      mainSectionsJson: value
    });

    try {
      if (value.trim()) {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed)) {
          setJsonError('Sections must be an array of section objects.');
        } else {
          setJsonError(null);
          setMainSections(parsed); // Sync visual state
        }
      } else {
        setJsonError(null);
        setMainSections([]);
      }
    } catch (err) {
      setJsonError(err.message);
    }
  };

  // Sync visual builder state helper
  const updateVisualSections = (newSections) => {
    setMainSections(newSections);
    setFormData(prev => ({
      ...prev,
      mainSectionsJson: JSON.stringify(newSections, null, 2)
    }));
    setJsonError(null);
  };

  const mutateSections = (fn) => {
    const cloned = JSON.parse(JSON.stringify(mainSections));
    fn(cloned);
    updateVisualSections(cloned);
  };

  // Visual Builder Handlers
  const updateValue = (path, value) => {
    mutateSections(draft => {
      const { parent, lastKey } = resolvePath(draft, path);
      parent[lastKey] = value;
    });
  };

  const handleAddNewItem = (parentPath, type) => {
    mutateSections(draft => {
      const { parent, lastKey } = resolvePath(draft, parentPath);
      if (!parent[lastKey]) parent[lastKey] = [];
      const array = parent[lastKey];
      if (type === 'subsection') {
        array.push({
          title: 'New Subsection',
          slug: 'new-subsection',
          description: '',
          overview: '',
          order: array.length + 1,
          isActive: true,
          subsections: [],
          subtopics: []
        });
      } else if (type === 'subtopic') {
        array.push({
          title: 'New Subtopic',
          slug: 'new-subtopic',
          description: '',
          order: array.length + 1,
          isActive: true,
          contentBlocks: [],
          codeExamples: [],
          images: []
        });
      } else if (type === 'contentBlock') {
        array.push({
          type: 'paragraph',
          content: '',
          links: [],
          order: array.length + 1
        });
      } else if (type === 'codeExample') {
        array.push({
          language: 'python',
          code: '',
          description: '',
          output: '',
          explanation: ''
        });
      } else if (type === 'image') {
        array.push({
          url: '',
          alt: '',
          caption: '',
          order: array.length + 1
        });
      }
    });
    toast.success(`Added new ${type}!`);
  };

  const handleDeleteItem = (path, name = 'item') => {
    if (window.confirm(`Are you sure you want to delete this ${name}?`)) {
      mutateSections(draft => {
        const itemIdx = path[path.length - 1];
        const parentPath = path.slice(0, -1);
        const { parent, lastKey } = resolvePath(draft, parentPath);
        parent[lastKey].splice(itemIdx, 1);
        
        // Reorder
        if (Array.isArray(parent[lastKey])) {
          parent[lastKey].forEach((item, idx) => {
            if (item && typeof item === 'object' && 'order' in item) {
              item.order = idx + 1;
            }
          });
        }
      });
      toast.info(`Deleted ${name}`);
    }
  };

  const handleMoveItem = (path, direction) => {
    mutateSections(draft => {
      const itemIdx = path[path.length - 1];
      const parentPath = path.slice(0, -1);
      const { parent, lastKey } = resolvePath(draft, parentPath);
      const array = parent[lastKey];
      const targetIdx = itemIdx + direction;
      if (targetIdx >= 0 && targetIdx < array.length) {
        const temp = array[itemIdx];
        array[itemIdx] = array[targetIdx];
        array[targetIdx] = temp;
        
        // Update order fields
        array.forEach((item, idx) => {
          if (item && typeof item === 'object' && 'order' in item) {
            item.order = idx + 1;
          }
        });
      }
    });
  };

  const handleAddSection = () => {
    mutateSections(draft => {
      draft.push({
        title: 'New Section',
        slug: 'new-section',
        description: '',
        overview: '',
        icon: '📚',
        order: draft.length + 1,
        isActive: true,
        subsections: []
      });
    });
    toast.success('Added new Main Section!');
  };

  const handleDeleteSection = (secIdx) => {
    if (window.confirm('Are you sure you want to delete this section and all its nested subsections/topics?')) {
      mutateSections(draft => {
        draft.splice(secIdx, 1);
      });
      toast.info('Deleted section');
    }
  };

  const handleMoveSection = (secIdx, direction) => {
    mutateSections(draft => {
      const targetIdx = secIdx + direction;
      if (targetIdx >= 0 && targetIdx < draft.length) {
        const temp = draft[secIdx];
        draft[secIdx] = draft[targetIdx];
        draft[targetIdx] = temp;
        
        draft.forEach((sec, idx) => {
          sec.order = idx + 1;
        });
      }
    });
  };

  const insertTemplate = (type) => {
    try {
      let currentVal = formData.mainSectionsJson.trim() || '[]';
      let parsed = JSON.parse(currentVal);
      if (!Array.isArray(parsed)) parsed = [];

      const template = Templates[type];
      
      if (type === 'section') {
        parsed.push(template);
      } else if (type === 'subsection') {
        if (parsed.length === 0) {
          parsed.push({
            ...Templates.section,
            subsections: [template]
          });
        } else {
          parsed[parsed.length - 1].subsections.push(template);
        }
      } else if (type === 'contentBlock' || type === 'codeExample') {
        if (parsed.length === 0) {
          parsed.push({
            ...Templates.section,
            subsections: [{
              ...Templates.subsection,
              subtopics: [{
                title: "Sample Topic",
                slug: "sample-topic",
                contentBlocks: type === 'contentBlock' ? [template] : [],
                codeExamples: type === 'codeExample' ? [template] : []
              }]
            }]
          });
        } else {
          let sec = parsed[parsed.length - 1];
          if (sec.subsections.length === 0) sec.subsections.push(Templates.subsection);
          let sub = sec.subsections[sec.subsections.length - 1];
          if (sub.subtopics.length === 0) {
            sub.subtopics.push({
              title: "Sample Topic",
              slug: "sample-topic",
              contentBlocks: [],
              codeExamples: []
            });
          }
          let topic = sub.subtopics[sub.subtopics.length - 1];
          if (type === 'contentBlock') topic.contentBlocks.push(template);
          if (type === 'codeExample') topic.codeExamples.push(template);
        }
      }

      setFormData({
        ...formData,
        mainSectionsJson: JSON.stringify(parsed, null, 2)
      });
      setMainSections(parsed); // Sync to visual builder state
      setJsonError(null);
      toast.info(`Template '${type}' inserted at the end!`);
    } catch (err) {
      toast.error('Failed to insert template: Ensure existing JSON is valid.');
    }
  };

  const openAddModal = () => {
    setIsEditing(false);
    setCurrentId(null);
    setFormData({
      courseId: courses[0]?._id || '',
      courseKey: '',
      title: '',
      description: '',
      overview: '',
      isPublished: false,
      mainSectionsJson: '[]'
    });
    setMainSections([]);
    setJsonError(null);
    setIsModalOpen(true);
    setShowAddCourse(false);
    setEditorTab('visual');
    setExpandedSections({});
    setExpandedSubsections({});
    setExpandedSubtopics({});
  };

  const openEditModal = (content) => {
    setIsEditing(true);
    setCurrentId(content._id);
    const initialJson = JSON.stringify(content.mainSections || [], null, 2);
    setFormData({
      courseId: content.courseId?._id || content.courseId || '',
      courseKey: content.courseKey,
      title: content.title,
      description: content.description || '',
      overview: content.overview || '',
      isPublished: content.isPublished || false,
      mainSectionsJson: initialJson
    });
    try {
      setMainSections(content.mainSections || []);
    } catch (err) {
      setMainSections([]);
    }
    setJsonError(null);
    setIsModalOpen(true);
    setShowAddCourse(false);
    setEditorTab('visual');
    setExpandedSections({});
    setExpandedSubsections({});
    setExpandedSubtopics({});
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course content?')) {
      try {
        const res = await axios.delete(`${apiUrl}/api/course-content/${id}`);
        if (res.data.success) {
          toast.success('Course content deleted successfully');
          fetchData();
        }
      } catch (error) {
        toast.error('Failed to delete course content');
        console.error(error);
      }
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    if (!newCourseName.trim()) {
      return toast.warning('Course name is required');
    }
    try {
      const res = await axios.post(`${apiUrl}/api/course-content/courses/create`, {
        title: newCourseName,
        description: newCourseDesc,
        categoryName: newCourseCategory || 'General'
      });
      if (res.data.success) {
        toast.success('New course created successfully!');
        const created = res.data.data;
        setCourses([created, ...courses]);
        setFormData({
          ...formData,
          courseId: created._id
        });
        setShowAddCourse(false);
        setNewCourseName('');
        setNewCourseDesc('');
        setNewCourseCategory('');
        
        // Refresh full data lists in background
        const resCourses = await axios.get(`${apiUrl}/api/course-content/courses/list`);
        if (resCourses.data.success) {
          setCourses(resCourses.data.data.courses);
          setCategories(resCourses.data.data.categories);
        }
      }
    } catch (error) {
      toast.error('Failed to create course');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (jsonError) {
      return toast.error('Please fix the JSON errors before saving.');
    }

    try {
      let mainSectionsPayload = [];
      if (formData.mainSectionsJson.trim()) {
        mainSectionsPayload = JSON.parse(formData.mainSectionsJson);
      }

      const payload = {
        courseId: formData.courseId,
        courseKey: formData.courseKey,
        title: formData.title,
        description: formData.description,
        overview: formData.overview,
        isPublished: formData.isPublished,
        mainSections: mainSectionsPayload
      };

      if (isEditing) {
        const res = await axios.put(`${apiUrl}/api/course-content/${currentId}`, payload);
        if (res.data.success) {
          toast.success('Course content updated successfully');
        }
      } else {
        const res = await axios.post(`${apiUrl}/api/course-content`, payload);
        if (res.data.success) {
          toast.success('Course content added successfully');
        }
      }

      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
      console.error(error);
    }
  };

  // Recursive Subsection Renderer
  const renderSubsection = (subsection, path, level = 1) => {
    const pathKey = path.join('-');
    const isExpanded = !!expandedSubsections[pathKey];
    const toggleExpand = () => {
      setExpandedSubsections(prev => ({
        ...prev,
        [pathKey]: !prev[pathKey]
      }));
    };

    return (
      <div key={pathKey} className="border-l-2 border-indigo-400 pl-3.5 py-1.5 my-2.5 bg-indigo-50/20 rounded-r-md">
        <div className="flex justify-between items-center bg-indigo-100/50 p-2 rounded-md">
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-indigo-900 text-[11px] uppercase tracking-wider">Lvl {level} Tab:</span>
            <span className="font-bold text-gray-800 text-xs">{subsection.title || 'Untitled Subsection'}</span>
            <span className="text-[10px] text-gray-400 font-mono">({subsection.slug})</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <button
              type="button"
              onClick={toggleExpand}
              className="text-[10px] text-indigo-700 hover:text-indigo-900 bg-white border px-2 py-0.5 rounded shadow-sm font-semibold"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </button>
            <button
              type="button"
              onClick={() => handleMoveItem(path, -1)}
              className="p-0.5 bg-white border rounded text-gray-500 hover:bg-gray-100 text-[10px] font-bold"
              title="Move Up"
            >
              ▲
            </button>
            <button
              type="button"
              onClick={() => handleMoveItem(path, 1)}
              className="p-0.5 bg-white border rounded text-gray-500 hover:bg-gray-100 text-[10px] font-bold"
              title="Move Down"
            >
              ▼
            </button>
            <button
              type="button"
              onClick={() => handleDeleteItem(path, 'Subsection')}
              className="p-0.5 bg-white border rounded text-red-500 hover:bg-red-50"
              title="Delete Subsection"
            >
              ✕
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-3.5 space-y-3.5 bg-white p-3.5 rounded-md border border-indigo-100 shadow-inner">
            <div className="grid grid-cols-2 gap-3.5">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase">Title</label>
                <input
                  type="text"
                  value={subsection.title}
                  onChange={e => {
                    updateValue([...path, 'title'], e.target.value);
                    updateValue([...path, 'slug'], slugify(e.target.value));
                  }}
                  className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase">Slug</label>
                <input
                  type="text"
                  value={subsection.slug}
                  onChange={e => updateValue([...path, 'slug'], slugify(e.target.value))}
                  className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-indigo-500 outline-none font-mono"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3.5">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase">Description</label>
                <textarea
                  value={subsection.description || ''}
                  onChange={e => updateValue([...path, 'description'], e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-indigo-500 outline-none h-14"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase">Overview</label>
                <textarea
                  value={subsection.overview || ''}
                  onChange={e => updateValue([...path, 'overview'], e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-indigo-500 outline-none h-14"
                />
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex items-center text-xs font-semibold text-gray-700 select-none">
                <input
                  type="checkbox"
                  checked={subsection.isActive !== false}
                  onChange={e => updateValue([...path, 'isActive'], e.target.checked)}
                  className="mr-1.5 h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                Subsection is Active
              </label>
            </div>

            {/* Child Subsections */}
            <div className="border border-dashed border-indigo-200 p-3 rounded bg-indigo-50/5">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[11px] font-bold text-indigo-900 uppercase">Nested Sub-tabs (Lvl {level + 1})</span>
                <button
                  type="button"
                  onClick={() => handleAddNewItem([...path, 'subsections'], 'subsection')}
                  className="text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-0.5 rounded shadow-sm font-semibold"
                >
                  + Add Nested Subsection
                </button>
              </div>
              {subsection.subsections && subsection.subsections.length > 0 ? (
                <div className="space-y-2">
                  {subsection.subsections.map((sub, idx) => 
                    renderSubsection(sub, [...path, 'subsections', idx], level + 1)
                  )}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No nested subsections at level {level + 1}.</p>
              )}
            </div>

            {/* Subtopics */}
            <div className="border border-dashed border-emerald-200 p-3 rounded bg-emerald-50/5">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[11px] font-bold text-emerald-800 uppercase">🎯 Topics in this Sub-tab</span>
                <button
                  type="button"
                  onClick={() => handleAddNewItem([...path, 'subtopics'], 'subtopic')}
                  className="text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded shadow-sm font-semibold"
                >
                  + Add Topic
                </button>
              </div>
              {subsection.subtopics && subsection.subtopics.length > 0 ? (
                <div className="space-y-3">
                  {subsection.subtopics.map((topic, idx) => 
                    renderSubtopic(topic, [...path, 'subtopics', idx])
                  )}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No topics inside this subsection. Add one to house detail blocks!</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Subtopic Renderer
  const renderSubtopic = (topic, path) => {
    const pathKey = path.join('-');
    const isExpanded = !!expandedSubtopics[pathKey];
    const toggleExpand = () => {
      setExpandedSubtopics(prev => ({
        ...prev,
        [pathKey]: !prev[pathKey]
      }));
    };

    const currentTab = subtopicTabs[pathKey] || 'blocks';
    const setTab = (tab) => {
      setSubtopicTabs(prev => ({
        ...prev,
        [pathKey]: tab
      }));
    };

    return (
      <div key={pathKey} className="border-l-2 border-emerald-400 pl-3.5 py-1.5 bg-emerald-50/10 rounded-r-md">
        <div className="flex justify-between items-center bg-emerald-100/40 p-2 rounded-md">
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-emerald-800 text-[10px] uppercase">Topic:</span>
            <span className="font-bold text-gray-800 text-xs">{topic.title || 'Untitled Topic'}</span>
            <span className="text-[10px] text-gray-400 font-mono">({topic.slug})</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <button
              type="button"
              onClick={toggleExpand}
              className="text-[10px] text-emerald-700 hover:text-emerald-900 bg-white border px-2 py-0.5 rounded shadow-sm font-semibold"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </button>
            <button
              type="button"
              onClick={() => handleMoveItem(path, -1)}
              className="p-0.5 bg-white border rounded text-gray-500 hover:bg-gray-100 text-[10px]"
              title="Move Up"
            >
              ▲
            </button>
            <button
              type="button"
              onClick={() => handleMoveItem(path, 1)}
              className="p-0.5 bg-white border rounded text-gray-500 hover:bg-gray-100 text-[10px]"
              title="Move Down"
            >
              ▼
            </button>
            <button
              type="button"
              onClick={() => handleDeleteItem(path, 'Topic')}
              className="p-0.5 bg-white border rounded text-red-500 hover:bg-red-50"
              title="Delete Topic"
            >
              ✕
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-3.5 space-y-3.5 bg-white p-3.5 rounded-md border border-emerald-100 shadow-inner">
            <div className="grid grid-cols-2 gap-3.5">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase">Title</label>
                <input
                  type="text"
                  value={topic.title}
                  onChange={e => {
                    updateValue([...path, 'title'], e.target.value);
                    updateValue([...path, 'slug'], slugify(e.target.value));
                  }}
                  className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-emerald-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase">Slug</label>
                <input
                  type="text"
                  value={topic.slug}
                  onChange={e => updateValue([...path, 'slug'], slugify(e.target.value))}
                  className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-emerald-500 outline-none font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase">Description</label>
              <textarea
                value={topic.description || ''}
                onChange={e => updateValue([...path, 'description'], e.target.value)}
                className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-emerald-500 outline-none h-14"
              />
            </div>

            {/* Subtopic Inner Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-3.5">
                <button
                  type="button"
                  onClick={() => setTab('blocks')}
                  className={`py-1.5 px-2.5 border-b-2 text-xs font-bold transition-all ${
                    currentTab === 'blocks'
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  📝 Content ({topic.contentBlocks?.length || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setTab('code')}
                  className={`py-1.5 px-2.5 border-b-2 text-xs font-bold transition-all ${
                    currentTab === 'code'
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  💻 Code Blocks ({topic.codeExamples?.length || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setTab('images')}
                  className={`py-1.5 px-2.5 border-b-2 text-xs font-bold transition-all ${
                    currentTab === 'images'
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  🖼️ Images ({topic.images?.length || 0})
                </button>
              </nav>
            </div>

            {/* Inner Tab Content */}
            <div className="pt-1.5">
              {currentTab === 'blocks' && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">Paragraphs & Headers</span>
                    <button
                      type="button"
                      onClick={() => handleAddNewItem([...path, 'contentBlocks'], 'contentBlock')}
                      className="text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded font-semibold"
                    >
                      + Add Content Block
                    </button>
                  </div>
                  {topic.contentBlocks && topic.contentBlocks.length > 0 ? (
                    <div className="space-y-3">
                      {topic.contentBlocks.map((block, blockIdx) => (
                        <div key={blockIdx} className="border border-gray-200 rounded p-3 bg-gray-50/50 space-y-2.5">
                          <div className="flex justify-between items-center">
                            <select
                              value={block.type || 'paragraph'}
                              onChange={e => updateValue([...path, 'contentBlocks', blockIdx, 'type'], e.target.value)}
                              className="border border-gray-300 rounded px-2 py-0.5 text-xs outline-none focus:ring-1 focus:ring-emerald-500 font-semibold text-gray-700"
                            >
                              {['paragraph', 'heading', 'subheading', 'list', 'bullet', 'numbered', 'quote', 'note', 'warning', 'tip', 'step', 'image', 'info'].map(t => (
                                <option key={t} value={t}>{t.toUpperCase()}</option>
                              ))}
                            </select>
                            <div className="flex items-center space-x-1">
                              <button
                                type="button"
                                onClick={() => handleMoveItem([...path, 'contentBlocks', blockIdx], -1)}
                                className="p-0.5 bg-white border rounded text-gray-400 hover:text-gray-600"
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMoveItem([...path, 'contentBlocks', blockIdx], 1)}
                                className="p-0.5 bg-white border rounded text-gray-400 hover:text-gray-600"
                              >
                                ▼
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteItem([...path, 'contentBlocks', blockIdx], 'Content Block')}
                                className="p-0.5 bg-white border rounded text-red-500 hover:bg-red-50"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          <textarea
                            value={block.content}
                            onChange={e => updateValue([...path, 'contentBlocks', blockIdx, 'content'], e.target.value)}
                            placeholder="Write block content here..."
                            className="w-full border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-emerald-500 outline-none h-16 text-xs font-sans leading-relaxed"
                          />
                          
                          {/* Block links list */}
                          <div className="border border-dashed border-gray-200 p-2.5 bg-white rounded space-y-1.5">
                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-bold text-gray-400 uppercase">Attached Links ({block.links?.length || 0})</span>
                              <button
                                type="button"
                                onClick={() => {
                                  mutateSections(draft => {
                                    const { parent, lastKey } = resolvePath(draft, [...path, 'contentBlocks', blockIdx, 'links']);
                                    if (!parent[lastKey]) parent[lastKey] = [];
                                    parent[lastKey].push({ text: 'Link Label', url: 'https://' });
                                  });
                                }}
                                className="text-[9px] bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-0.5 rounded font-semibold"
                              >
                                + Link
                              </button>
                            </div>
                            {block.links && block.links.map((link, linkIdx) => (
                              <div key={linkIdx} className="flex space-x-2 items-center">
                                <input
                                  type="text"
                                  value={link.text}
                                  onChange={e => updateValue([...path, 'contentBlocks', blockIdx, 'links', linkIdx, 'text'], e.target.value)}
                                  placeholder="Text label"
                                  className="w-1/3 text-[10px] border border-gray-300 rounded px-2 py-1"
                                />
                                <input
                                  type="text"
                                  value={link.url}
                                  onChange={e => updateValue([...path, 'contentBlocks', blockIdx, 'links', linkIdx, 'url'], e.target.value)}
                                  placeholder="URL link"
                                  className="w-2/3 text-[10px] border border-gray-300 rounded px-2 py-1 font-mono"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    mutateSections(draft => {
                                      const { parent, lastKey } = resolvePath(draft, [...path, 'contentBlocks', blockIdx, 'links']);
                                      parent[lastKey].splice(linkIdx, 1);
                                    });
                                  }}
                                  className="text-red-500 text-[10px] hover:text-red-700 font-bold px-1"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400 italic">No content blocks yet.</p>
                  )}
                </div>
              )}

              {currentTab === 'code' && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">Code Blocks</span>
                    <button
                      type="button"
                      onClick={() => handleAddNewItem([...path, 'codeExamples'], 'codeExample')}
                      className="text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded font-semibold"
                    >
                      + Add Code Block
                    </button>
                  </div>
                  {topic.codeExamples && topic.codeExamples.length > 0 ? (
                    <div className="space-y-3.5">
                      {topic.codeExamples.map((codeEx, codeIdx) => (
                        <div key={codeIdx} className="border border-gray-200 rounded p-3 bg-gray-50/50 space-y-2">
                          <div className="flex justify-between items-center">
                            <select
                              value={codeEx.language || 'python'}
                              onChange={e => updateValue([...path, 'codeExamples', codeIdx, 'language'], e.target.value)}
                              className="border border-gray-300 rounded px-2 py-0.5 text-xs outline-none focus:ring-1 focus:ring-emerald-500 font-semibold text-gray-700"
                            >
                              {['python', 'cpp', 'c', 'bash', 'javascript', 'other'].map(l => (
                                <option key={l} value={l}>{l.toUpperCase()}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleDeleteItem([...path, 'codeExamples', codeIdx], 'Code Example')}
                              className="p-0.5 bg-white border rounded text-red-500 hover:bg-red-50"
                            >
                              ✕
                            </button>
                          </div>
                          <div>
                            <input
                              type="text"
                              value={codeEx.description || ''}
                              onChange={e => updateValue([...path, 'codeExamples', codeIdx, 'description'], e.target.value)}
                              placeholder="Description title..."
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          </div>
                          <div>
                            <textarea
                              value={codeEx.code}
                              onChange={e => updateValue([...path, 'codeExamples', codeIdx, 'code'], e.target.value)}
                              placeholder="Code content..."
                              className="w-full border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-emerald-500 outline-none h-24 font-mono text-xs bg-slate-900 text-emerald-400"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[9px] font-bold text-gray-400 uppercase mb-0.5">Console Output</label>
                              <textarea
                                value={codeEx.output || ''}
                                onChange={e => updateValue([...path, 'codeExamples', codeIdx, 'output'], e.target.value)}
                                placeholder="Output..."
                                className="w-full border border-gray-300 rounded px-2 py-1 font-mono text-[10px] h-12 bg-slate-800 text-slate-100"
                              />
                            </div>
                            <div>
                              <label className="block text-[9px] font-bold text-gray-400 uppercase mb-0.5">Explanation</label>
                              <textarea
                                value={codeEx.explanation || ''}
                                onChange={e => updateValue([...path, 'codeExamples', codeIdx, 'explanation'], e.target.value)}
                                placeholder="Line explanation..."
                                className="w-full border border-gray-300 rounded px-2 py-1 text-[10px] h-12"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400 italic">No code blocks yet.</p>
                  )}
                </div>
              )}

              {currentTab === 'images' && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">Images</span>
                    <button
                      type="button"
                      onClick={() => handleAddNewItem([...path, 'images'], 'image')}
                      className="text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded font-semibold"
                    >
                      + Add Image
                    </button>
                  </div>
                  {topic.images && topic.images.length > 0 ? (
                    <div className="space-y-3">
                      {topic.images.map((img, imgIdx) => (
                        <div key={imgIdx} className="border border-gray-200 rounded p-3 bg-gray-50/50 space-y-2">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleDeleteItem([...path, 'images', imgIdx], 'Image')}
                              className="p-0.5 bg-white border rounded text-red-500 hover:bg-red-50"
                            >
                              ✕
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={img.url}
                              onChange={e => updateValue([...path, 'images', imgIdx, 'url'], e.target.value)}
                              placeholder="Image url (Cloudinary)"
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            />
                            <input
                              type="text"
                              value={img.alt || ''}
                              onChange={e => updateValue([...path, 'images', imgIdx, 'alt'], e.target.value)}
                              placeholder="Alt text"
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={img.caption || ''}
                              onChange={e => updateValue([...path, 'images', imgIdx, 'caption'], e.target.value)}
                              placeholder="Caption text"
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            />
                            <input
                              type="number"
                              value={img.order || 0}
                              onChange={e => updateValue([...path, 'images', imgIdx, 'order'], parseInt(e.target.value) || 0)}
                              placeholder="Display Order"
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400 italic">No images yet.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Course Contents</h1>
          <p className="text-gray-600 mt-1">Manage sections, subsections, and topic blocks of your courses.</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow flex items-center transition-colors font-medium"
        >
          <PlusIcon /> Add Course Content
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : contents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500 border border-gray-200">
          No course content entries found. Add a new course content block to get started.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Course Key</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sections count</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contents.map((item) => (
                <tr key={item._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-semibold text-gray-900">{item.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{item.courseId?.title || 'Unknown Course'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono bg-gray-100 text-gray-800 px-2 py-0.5 rounded border">
                      {item.courseKey}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-950 font-medium">
                      📂 {item.mainSections?.length || 0} Main Section(s)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.isPublished 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}>
                      {item.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(item)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4 inline-flex items-center"
                      title="Edit"
                    >
                      <EditIcon />
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="text-red-600 hover:text-red-900 inline-flex items-center"
                      title="Delete"
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Main Form Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl my-8 flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b bg-gray-50 rounded-t-lg">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {isEditing ? 'Edit Course Content' : 'Add Course Content'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Fill in details and manage course sections structure.</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 border rounded p-1 hover:bg-gray-100"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Split Screen Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* Left Side: General Fields (5 cols) */}
              <div className="lg:col-span-5 space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">1. Metadata</h3>

                {/* Course Selection or Addition */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">Course Link</label>
                    <button
                      type="button"
                      onClick={() => setShowAddCourse(!showAddCourse)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                    >
                      {showAddCourse ? 'Cancel' : '+ Create New Course'}
                    </button>
                  </div>

                  {showAddCourse ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-3">
                      <p className="text-xs font-semibold text-blue-800">Create a Course to link with:</p>
                      <div>
                        <input
                          type="text"
                          placeholder="Course Title (e.g. ROS 2 Beginners)"
                          value={newCourseName}
                          onChange={(e) => setNewCourseName(e.target.value)}
                          className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Category (e.g. Robotics)"
                          value={newCourseCategory}
                          onChange={(e) => setNewCourseCategory(e.target.value)}
                          className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <textarea
                          placeholder="Course Description"
                          value={newCourseDesc}
                          onChange={(e) => setNewCourseDesc(e.target.value)}
                          className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 h-16"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleCreateCourse}
                        className="bg-blue-600 text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-blue-700 w-full"
                      >
                        Save & Link Course
                      </button>
                    </div>
                  ) : (
                    <select
                      name="courseId"
                      value={formData.courseId}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Select Course --</option>
                      {courses.map(c => (
                        <option key={c._id} value={c._id}>
                          {c.title} ({c.category?.name || 'General'})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course Key (Unique slug)</label>
                  <input
                    type="text"
                    name="courseKey"
                    value={formData.courseKey}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="e.g. ros2-complete-guide"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content/Display Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. ROS 2 Complete Guide & Syllabus"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description Summary</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 h-20"
                    placeholder="Brief intro..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overview Details</label>
                  <textarea
                    name="overview"
                    value={formData.overview}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 h-20"
                    placeholder="Overview of what is covered..."
                  />
                </div>

                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    id="isPublished"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900 font-medium select-none">
                    Publish immediately (Make visible on main site)
                  </label>
                </div>
              </div>

              {/* Right Side: Visual or JSON Editor (7 cols) */}
              <div className="lg:col-span-7 flex flex-col">
                <div className="flex justify-between items-center border-b pb-2 mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">2. Course Structure</h3>
                  
                  {/* Tab Selector */}
                  <div className="flex border rounded overflow-hidden text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        if (jsonError) {
                          toast.error("Please fix JSON errors before switching.");
                          return;
                        }
                        setEditorTab('visual');
                      }}
                      className={`px-3 py-1 font-semibold ${
                        editorTab === 'visual' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      🛠️ Visual Builder
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorTab('json')}
                      className={`px-3 py-1 font-semibold ${
                        editorTab === 'json' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      📝 JSON Editor
                    </button>
                  </div>
                </div>

                {editorTab === 'visual' ? (
                  /* Visual Builder Section */
                  <div className="flex-1 overflow-y-auto space-y-4 max-h-[60vh] pr-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-500">
                        {mainSections.length} Main Section(s) configured
                      </span>
                      <button
                        type="button"
                        onClick={handleAddSection}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded shadow flex items-center transition-colors font-medium"
                      >
                        <PlusIcon /> Add Main Section
                      </button>
                    </div>

                    {mainSections.length === 0 ? (
                      <div className="border border-dashed rounded-lg p-8 text-center text-gray-400 bg-gray-50 text-xs">
                        No sections yet. Click "Add Main Section" to begin organizing your course content visually!
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {mainSections.map((sec, secIdx) => {
                          const isExpanded = !!expandedSections[secIdx];
                          const toggleSec = () => {
                            setExpandedSections(prev => ({ ...prev, [secIdx]: !prev[secIdx] }));
                          };

                          return (
                            <div key={secIdx} className="border border-blue-200 rounded-lg bg-blue-50/10 overflow-hidden shadow-sm">
                              {/* Section Header */}
                              <div className="flex justify-between items-center bg-blue-50 border-b border-blue-100 p-3">
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">{sec.icon || '📚'}</span>
                                  <span className="font-bold text-blue-900 text-sm">{sec.title || 'Untitled Section'}</span>
                                  <span className="text-xs text-gray-400 font-mono">({sec.slug})</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={toggleSec}
                                    className="text-xs text-blue-700 hover:text-blue-900 bg-white border px-2.5 py-1 rounded shadow-sm font-medium"
                                  >
                                    {isExpanded ? 'Collapse' : 'Expand'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleMoveSection(secIdx, -1)}
                                    className="p-1 bg-white border rounded hover:bg-gray-50 text-gray-600 text-xs px-2"
                                    title="Move Up"
                                  >
                                    ▲
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleMoveSection(secIdx, 1)}
                                    className="p-1 bg-white border rounded hover:bg-gray-50 text-gray-600 text-xs px-2"
                                    title="Move Down"
                                  >
                                    ▼
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteSection(secIdx)}
                                    className="p-1 bg-white border rounded hover:bg-red-50 text-red-600"
                                    title="Delete Section"
                                  >
                                    <TrashIcon />
                                  </button>
                                </div>
                              </div>

                              {/* Section Content */}
                              {isExpanded && (
                                <div className="p-4 space-y-4 bg-white border-t border-blue-100">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div className="md:col-span-2">
                                      <label className="block text-xs font-semibold text-gray-700 mb-1">Section Title</label>
                                      <input
                                        type="text"
                                        value={sec.title}
                                        onChange={e => {
                                          updateValue([secIdx, 'title'], e.target.value);
                                          updateValue([secIdx, 'slug'], slugify(e.target.value));
                                        }}
                                        className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none"
                                        placeholder="Section title..."
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-700 mb-1">Section Icon</label>
                                      <input
                                        type="text"
                                        value={sec.icon || ''}
                                        onChange={e => updateValue([secIdx, 'icon'], e.target.value)}
                                        className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none"
                                        placeholder="Emoji, e.g. 🚀"
                                      />
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-700 mb-1">Slug</label>
                                      <input
                                        type="text"
                                        value={sec.slug}
                                        onChange={e => updateValue([secIdx, 'slug'], slugify(e.target.value))}
                                        className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                                      />
                                    </div>
                                    <div className="flex items-center pt-5">
                                      <label className="flex items-center text-xs font-semibold text-gray-700 select-none">
                                        <input
                                          type="checkbox"
                                          checked={sec.isActive !== false}
                                          onChange={e => updateValue([secIdx, 'isActive'], e.target.checked)}
                                          className="mr-1 h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        Section is Active
                                      </label>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                                      <textarea
                                        value={sec.description || ''}
                                        onChange={e => updateValue([secIdx, 'description'], e.target.value)}
                                        className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none h-16"
                                        placeholder="Section description..."
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-700 mb-1">Overview</label>
                                      <textarea
                                        value={sec.overview || ''}
                                        onChange={e => updateValue([secIdx, 'overview'], e.target.value)}
                                        className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none h-16"
                                        placeholder="What is covered in this section..."
                                      />
                                    </div>
                                  </div>

                                  {/* Subsections list within section */}
                                  <div className="border border-dashed border-blue-200 p-3.5 rounded-lg bg-blue-50/5 mt-4">
                                    <div className="flex justify-between items-center mb-3">
                                      <span className="text-xs font-bold text-blue-900">📄 Subsections (Sub-tabs)</span>
                                      <button
                                        type="button"
                                        onClick={() => handleAddNewItem([secIdx, 'subsections'], 'subsection')}
                                        className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded shadow-sm flex items-center font-medium"
                                      >
                                        + Add Subsection
                                      </button>
                                    </div>
                                    {sec.subsections && sec.subsections.length > 0 ? (
                                      <div className="space-y-3">
                                        {sec.subsections.map((sub, subIdx) => 
                                          renderSubsection(sub, [secIdx, 'subsections', subIdx], 1)
                                        )}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-400 italic text-center py-2 bg-gray-50 border rounded border-dashed">
                                        No subsections created yet. Add one to start organizing content.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  /* JSON Editor Section (existing textarea) */
                  <div className="flex-1 flex flex-col">
                    {/* Templates Insertion Toolbar */}
                    <div className="bg-gray-100 border rounded-md p-3 mb-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">⚡ Quick Templates Insert Toolbar:</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => insertTemplate('section')}
                          className="bg-white border text-gray-700 text-xs px-2.5 py-1.5 rounded hover:bg-gray-50 font-medium shadow-sm transition-colors"
                        >
                          📁 + Section
                        </button>
                        <button
                          type="button"
                          onClick={() => insertTemplate('subsection')}
                          className="bg-white border text-gray-700 text-xs px-2.5 py-1.5 rounded hover:bg-gray-50 font-medium shadow-sm transition-colors"
                        >
                          📄 + Subsection & Topic
                        </button>
                        <button
                          type="button"
                          onClick={() => insertTemplate('contentBlock')}
                          className="bg-white border text-gray-700 text-xs px-2.5 py-1.5 rounded hover:bg-gray-50 font-medium shadow-sm transition-colors"
                        >
                          ✏️ + Paragraph Block
                        </button>
                        <button
                          type="button"
                          onClick={() => insertTemplate('codeExample')}
                          className="bg-white border text-gray-700 text-xs px-2.5 py-1.5 rounded hover:bg-gray-50 font-medium shadow-sm transition-colors"
                        >
                          💻 + Code Block
                        </button>
                      </div>
                    </div>

                    {/* JSON Editor Textarea */}
                    <div className="flex-1 flex flex-col min-h-[300px]">
                      <textarea
                        name="mainSectionsJson"
                        value={formData.mainSectionsJson}
                        onChange={handleJsonChange}
                        className={`flex-1 w-full border rounded-md p-4 font-mono text-xs leading-relaxed outline-none focus:ring-2 ${
                          jsonError ? 'border-red-400 focus:ring-red-400 bg-red-50' : 'border-gray-300 focus:ring-blue-500'
                        }`}
                        placeholder="[ ... ]"
                        style={{ whiteSpace: 'pre', overflowWrap: 'normal', overflowX: 'auto', minHeight: '300px' }}
                      />

                      {jsonError && (
                        <div className="mt-2 text-xs text-red-600 bg-red-100 border border-red-200 rounded p-2.5 font-mono">
                          ⚠️ JSON Parse Error: {jsonError}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t bg-gray-50 rounded-b-lg flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!!jsonError}
                className={`px-5 py-2.5 text-white rounded-md font-semibold shadow focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  jsonError 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isEditing ? 'Update Course Content' : 'Save Course Content'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseContent;
